import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import type { TenantThemeConfig } from '../types/theme'
import * as authService from '../services/authService'
import {
  pendingDepositDraftService,
  type PendingDepositMethod,
  type PendingDepositSenderAccount,
} from '../services/pendingDepositDraftService'
import {
  getDepositAccountSourceMode,
  getPublicDepositAccounts,
  type PublicDepositAccount,
} from '../services/depositAccountService'
import { getDepositFlowConfig, type DepositFlowConfig } from '../services/depositFlowService'
import {
  assertDepositResponseMatchesDraft,
  attachSlipToDepositTransaction,
  createDeposit,
  getTransactionDetails,
  type WalletTransaction,
} from '../services/walletTransactionService'
import { slipStorageService } from '../services/slipStorageService'
import {
  prepareSlipFileForUpload,
  SLIP_UPLOAD_HINT,
  slipCompressedMessage,
} from '@huay/shared/slipImage'
import './ModernTopupSlipPage.css'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernTopupSlipPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

const DEFAULT_DEPOSIT_FLOW: DepositFlowConfig = {
  tenantId: '',
  tenantName: '',
  paymentServiceSelected: false,
  paymentGatewayEnabled: false,
  slipServiceSelected: false,
  slipVerificationEnabled: false,
  effectiveDepositMode: 'manual',
  depositAutoCreditEnabled: true,
  depositAutoCreditViaPayment: true,
  depositAutoCreditViaSlip: true,
  depositAutoCreditViaSms: true,
  automationSettingsWarning: null,
}

const getMethodLabel = (method: PendingDepositMethod, paymentGatewayEnabled = false): string => {
  if (paymentGatewayEnabled && method === 'bank_transfer') return 'Payment'
  if (method === 'true_money') return 'TrueMoney Wallet'
  if (method === 'prompt_pay') return 'PromptPay'
  if (method === 'decimal_reservation') return 'ฝากทศนิยม'
  return 'โอนผ่านธนาคาร'
}

const showToast = (icon: 'success' | 'error' | 'warning' | 'info', title: string) =>
  Swal.fire({
    toast: true,
    position: 'top',
    timer: 3000,
    showConfirmButton: false,
    icon,
    title,
  })

const panelStyle: React.CSSProperties = {
  background:
    'radial-gradient(ellipse 80% 45% at 12% 0%, color-mix(in srgb, var(--htd-primary) 22%, transparent), transparent 58%), linear-gradient(180deg, rgba(12, 24, 52, 0.96) 0%, rgba(9, 16, 36, 0.96) 100%)',
  border: '1px solid rgba(125, 211, 252, 0.24)',
  boxShadow: '0 18px 38px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
}

const innerSurfaceStyle: React.CSSProperties = {
  background: 'rgba(248, 250, 252, 0.98)',
  boxShadow: '0 14px 30px rgba(2, 8, 23, 0.12), inset 0 1px 0 rgba(255,255,255,0.9)',
}

const valueCardStyle: React.CSSProperties = {
  ...innerSurfaceStyle,
  borderRadius: 22,
  padding: '1rem 1rem',
}

const lightSurfaceLabelStyle: React.CSSProperties = {
  color: '#64748b',
  fontWeight: 600,
}

const lightSurfaceMutedTextStyle: React.CSSProperties = {
  color: '#475569',
  fontWeight: 500,
}

const lightSurfaceHeadingStyle: React.CSSProperties = {
  color: '#0f172a',
}

const stepCardStyle = (active: boolean): React.CSSProperties => ({
  borderRadius: 18,
  padding: '0.95rem 1rem',
  border: active ? '1px solid rgba(125, 211, 252, 0.45)' : '1px solid rgba(125, 211, 252, 0.16)',
  background: active
    ? 'linear-gradient(135deg, var(--htd-primary) 0%, var(--htd-secondary) 100%)'
    : 'rgba(248, 250, 252, 0.96)',
  boxShadow: active ? '0 16px 30px color-mix(in srgb, var(--htd-primary) 30%, transparent)' : '0 10px 24px rgba(2, 8, 23, 0.1)',
})

const secondaryActionButtonStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(125, 211, 252, 0.22)',
  boxShadow: 'none',
}

const buildDepositAccountFromTransaction = (transaction?: WalletTransaction | null): PublicDepositAccount | null => {
  if (!transaction) return null
  const bankName = String(transaction.metadata?.selectedDepositBankName || '').trim()
  const accountName = String(transaction.metadata?.selectedDepositAccountName || '').trim()
  const accountNumber = String(transaction.metadata?.selectedDepositAccountNumber || '').trim()
  if (!bankName || !accountNumber) return null

  const rawDepositSourceMode = String(
    transaction.metadata?.selectedDepositSourceMode || transaction.metadata?.receiverAccount?.depositSourceMode || ''
  ).trim().toLowerCase()
  const depositSourceMode = rawDepositSourceMode === 'slip_only' || rawDepositSourceMode === 'sms_only'
    ? rawDepositSourceMode
    : undefined

  return {
    _id: String(transaction.metadata?.selectedDepositAccountId || transaction._id || '').trim() || transaction._id,
    accountName,
    bankName,
    bankCode: String(transaction.metadata?.selectedDepositBankCode || '').trim(),
    accountType: 'savings',
    isActive: true,
    isDefault: false,
    currency: 'THB',
    accountNumber,
    depositSourceMode,
  }
}

const buildSenderAccountSnapshot = (account?: authService.UserBankAccount | null): PendingDepositSenderAccount | undefined => {
  if (!account) return undefined
  return {
    bankCode: account.bankCode || '',
    bankName: account.bankName || '',
    accountNumber: account.accountNumber || '',
    accountName: account.accountName || '',
  }
}

const resolveLiveDepositAccount = (
  accounts: PublicDepositAccount[],
  account?: PublicDepositAccount | null
): PublicDepositAccount | null => {
  if (!account) return null
  return accounts.find((item) => item._id === account._id)
    || accounts.find((item) =>
      item.accountNumber === account.accountNumber
      && String(item.bankCode || '').toUpperCase() === String(account.bankCode || '').toUpperCase()
    )
    || null
}

const ModernTopupSlipPage: React.FC<ModernTopupSlipPageProps> = ({ themeConfig }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [depositFlow, setDepositFlow] = useState<DepositFlowConfig>(DEFAULT_DEPOSIT_FLOW)
  const [flowLoaded, setFlowLoaded] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [existingTransaction, setExistingTransaction] = useState<WalletTransaction | null>(null)
  const [depositAccounts, setDepositAccounts] = useState<PublicDepositAccount[]>([])
  const [userBankAccounts, setUserBankAccounts] = useState<authService.UserBankAccount[]>([])
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const currentUser = authService.getCurrentUser()
  const tenantId = currentUser?.tenantId || themeConfig.tenantId || ''
  const userId = currentUser?.id || ''
  const username = currentUser?.username || ''
  const draftId = searchParams.get('draft')?.trim() || ''
  const transactionId = searchParams.get('transaction')?.trim() || ''
  const hasTransactionParam = Boolean(transactionId)
  const handleCopyAccountNumber = useCallback((accountNumber?: string | null) => {
    if (!accountNumber) return
    navigator.clipboard?.writeText(accountNumber).catch(() => {})
  }, [])

  const restoredDraft = useMemo(() => {
    if (!tenantId || !userId || !draftId) return null
    return pendingDepositDraftService.getById(tenantId, userId, draftId)
  }, [draftId, tenantId, userId])

  const amount = String(location.state?.amount || restoredDraft?.amount || existingTransaction?.amount || '')
  const depositAccount =
    (location.state?.depositAccount as PublicDepositAccount | undefined)
    || restoredDraft?.depositAccount
    || buildDepositAccountFromTransaction(existingTransaction)
  const depositMethod =
    (location.state?.depositMethod as PendingDepositMethod | undefined)
    || restoredDraft?.depositMethod
    || (existingTransaction?.metadata?.depositMethod as PendingDepositMethod | undefined)
    || 'bank_transfer'
  const isAttachSlipMode = hasTransactionParam
  const isPaymentAttachMode = isAttachSlipMode && Boolean(existingTransaction?.metadata?.paymentGateway)
  const isPromptPaySlipMode = depositMethod === 'prompt_pay' && !isPaymentAttachMode
  const requestedPaymentGateway = Boolean(location.state?.usesPaymentGateway || restoredDraft?.usesPaymentGateway)
  const shouldUsePaymentGateway =
    !isAttachSlipMode && depositMethod === 'bank_transfer' && (requestedPaymentGateway || (flowLoaded && depositFlow.paymentGatewayEnabled))
  const paymentGateway = existingTransaction?.metadata?.paymentGateway
  const displayMethodLabel = getMethodLabel(depositMethod, isPaymentAttachMode)
  const resolvedDepositAccount = useMemo(() => {
    if (depositMethod !== 'bank_transfer') return depositAccount
    if (depositAccount && depositAccounts.length > 0) {
      return resolveLiveDepositAccount(depositAccounts, depositAccount) || depositAccount
    }
    return depositAccount
  }, [depositAccount, depositAccounts, depositMethod])
  const selectedDepositSourceMode = useMemo(
    () => getDepositAccountSourceMode(resolvedDepositAccount),
    [resolvedDepositAccount]
  )
  const isSmsOnlyManualDeposit = !isPaymentAttachMode && selectedDepositSourceMode === 'sms_only'
  const allowSlipAttachment = isPaymentAttachMode || !isSmsOnlyManualDeposit
  const submitWithoutSlipAllowed = !isPaymentAttachMode && isSmsOnlyManualDeposit
  const selectedUserBankAccount = userBankAccounts[0] ?? null
  const stateSenderAccount = location.state?.senderAccount as PendingDepositSenderAccount | undefined
  const selectedSenderAccount = stateSenderAccount
    || restoredDraft?.senderAccount
    || buildSenderAccountSnapshot(selectedUserBankAccount)
  const slipStepLabels = useMemo(() => {
    if (isPaymentAttachMode) return ['เลือกยอดฝาก', 'ชำระผ่าน Payment', 'แนบสลิป']
    if (isAttachSlipMode) return []
    if (isSmsOnlyManualDeposit) return ['เลือกยอดฝาก', 'ส่งผ่าน SMS']
    return ['เลือกยอดฝาก', 'แนบสลิป']
  }, [isAttachSlipMode, isPaymentAttachMode, isSmsOnlyManualDeposit])
  const slipStepIndex = slipStepLabels.length > 0 ? slipStepLabels.length - 1 : 0
  const slipStepTotal = slipStepLabels.length || 2
  const showManualCustomerAccountCard = !isAttachSlipMode

  useEffect(() => {
    if (!draftId && !transactionId) {
      navigate('/topup', { replace: true })
      return
    }

    if (!tenantId) {
      setFlowLoaded(true)
      return
    }

    Promise.all([
      getDepositFlowConfig(tenantId),
      getPublicDepositAccounts(tenantId, { forceRefresh: true }),
      transactionId ? getTransactionDetails(transactionId) : Promise.resolve(null),
      authService.fetchUserBankAccounts(),
    ])
      .then(([flow, publicDepositAccounts, transactionResponse, bankAccounts]) => {
        if (draftId && !transactionId && !restoredDraft) {
          navigate('/topup', { replace: true })
          return
        }
        if (transactionId) {
          const nextTransaction = transactionResponse?.data?.transaction || null
          if (!nextTransaction || nextTransaction.type !== 'deposit') {
            navigate('/history/deposit', { replace: true })
            return
          }
          setExistingTransaction(nextTransaction)
        }
        setUserBankAccounts(Array.isArray(bankAccounts) ? bankAccounts : [])
        setDepositAccounts(Array.isArray(publicDepositAccounts) ? publicDepositAccounts : [])
        setDepositFlow(flow)
      })
      .finally(() => {
        setFlowLoaded(true)
      })
  }, [draftId, navigate, restoredDraft, tenantId, transactionId])

  useEffect(() => {
    if (!flowLoaded) return
    if (shouldUsePaymentGateway) {
      navigate(`/topup/confirm?draft=${encodeURIComponent(draftId)}`, {
        replace: true,
        state: { amount, depositAccount, depositMethod, usesPaymentGateway: true },
      })
      return
    }
    if (!resolvedDepositAccount && !isAttachSlipMode) {
      navigate('/topup', { replace: true })
    }
  }, [amount, depositMethod, draftId, flowLoaded, isAttachSlipMode, navigate, resolvedDepositAccount, shouldUsePaymentGateway])

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const validateAndSetFile = async (file: File, onInvalid?: () => void) => {
    try {
      const result = await prepareSlipFileForUpload(file)
      if (!result.ok) {
        void showToast('error', result.message)
        onInvalid?.()
        return
      }

      setSelectedFile(result.file)
      if (previewUrl) URL.revokeObjectURL(previewUrl)
      setPreviewUrl(URL.createObjectURL(result.file))
      if (result.compressed) {
        void showToast('info', slipCompressedMessage(result.originalSize, result.file.size))
      }
    } catch {
      void showToast('error', 'ไม่สามารถประมวลผลไฟล์สลิปได้ กรุณาลองใหม่')
      onInvalid?.()
    }
  }

  const submitDeposit = async (attachSlip: boolean) => {
    if (!tenantId || !userId || !username) {
      void showToast('error', 'ไม่พบข้อมูลผู้ใช้')
      return
    }
    if (!isAttachSlipMode && !resolvedDepositAccount) {
      void showToast('error', 'ไม่พบบัญชีฝาก')
      return
    }
    if (!amount || Number(amount) <= 0) {
      void showToast('error', 'จำนวนเงินไม่ถูกต้อง')
      return
    }
    if (attachSlip && !allowSlipAttachment) {
      void showToast('error', 'บัญชีรับฝากนี้ตั้งค่าเป็น SMS เท่านั้น จึงไม่สามารถแนบสลิปได้')
      return
    }
    if (!attachSlip && !submitWithoutSlipAllowed) {
      void showToast('error', 'รายการฝากนี้ต้องแนบสลิปก่อนส่ง')
      return
    }
    if (attachSlip && !selectedFile) {
      void showToast('error', 'กรุณาแนบสลิปก่อนส่งรายการ')
      return
    }

    const activeDepositAccount = resolvedDepositAccount

    setSubmitting(true)
    try {
      let uploadedSlipUrl = ''
      if (attachSlip && selectedFile) {
        const transferTime = new Date()
        const uploadResult = await slipStorageService.uploadSlip(tenantId, {
          userId,
          username,
          transferAmount: Number(amount),
          transferTime,
          transferDate: transferTime,
          slipFile: selectedFile,
        })

        if (!uploadResult.success) {
          throw new Error(uploadResult.message || 'อัปโหลดสลิปไม่สำเร็จ')
        }

        const uploadData = Array.isArray(uploadResult.data) ? uploadResult.data[0] : uploadResult.data
        uploadedSlipUrl = String(uploadData?.storageUrl || '')
      }

      const result = isAttachSlipMode
        ? await attachSlipToDepositTransaction(transactionId, {
            slipData: {
              imageUrl: uploadedSlipUrl,
              bankCode: depositAccount?.bankCode,
              accountNumber: depositAccount?.accountNumber,
            },
          })
        : await createDeposit({
            amount: Number(amount),
            paymentMethod: 'bank_transfer',
            reference: draftId,
            ...(uploadedSlipUrl
              ? {
                  slipData: {
                    imageUrl: uploadedSlipUrl,
                    bankCode: activeDepositAccount?.bankCode,
                    accountNumber: activeDepositAccount?.accountNumber,
                  },
                }
              : {}),
            metadata: {
              note: attachSlip ? 'user-modern submitted deposit with slip attachment' : 'user-modern submitted deposit without slip attachment',
              depositMethod,
              selectedDepositAccountId: activeDepositAccount?._id,
              selectedDepositAccountNumber: activeDepositAccount?.accountNumber,
              selectedDepositAccountName: activeDepositAccount?.accountName,
              selectedDepositBankCode: activeDepositAccount?.bankCode,
              selectedDepositBankName: activeDepositAccount?.bankName,
              selectedDepositSourceMode,
              senderAccount: selectedSenderAccount,
              receiverAccount: activeDepositAccount ? {
                accountId: activeDepositAccount._id,
                bankCode: activeDepositAccount.bankCode,
                bankName: activeDepositAccount.bankName,
                accountNumber: activeDepositAccount.accountNumber,
                accountName: activeDepositAccount.accountName,
                depositSourceMode: selectedDepositSourceMode,
              } : undefined,
              sourceChannel: attachSlip ? 'slip' : 'sms',
              submittedSlipImageUrl: uploadedSlipUrl || undefined,
            },
          })

      if (!result.success) {
        throw new Error(result.message || 'ส่งรายการฝากไม่สำเร็จ')
      }

      if (!isAttachSlipMode) {
        assertDepositResponseMatchesDraft(result, draftId)
      }

      if (draftId) {
        pendingDepositDraftService.remove(tenantId, userId, draftId)
      }

      const nextMessage =
        result.data?.status === 'completed'
          ? 'ระบบเติมเครดิตให้เรียบร้อยแล้ว'
          : result.data?.approval?.required
            ? 'ระบบรับรายการฝากแล้ว และส่งต่อให้แอดมินตรวจสอบ'
            : result.recoveredExistingDeposit
              ? 'ระบบพบรายการฝากเดิมของคุณที่ส่งไว้แล้ว และรายการยังรอการตรวจสอบอยู่'
              : result.message || (isAttachSlipMode ? 'แนบสลิปเรียบร้อยแล้ว' : 'ส่งรายการฝากเรียบร้อยแล้ว')

      void showToast('success', nextMessage)
      navigate('/history/deposit', {
        replace: true,
        state: {
          depositJustSubmitted: true,
          depositSuccessMessage: nextMessage,
          depositPreferredQueue:
            result.data?.status === 'completed'
              ? 'completed'
              : 'pending',
        },
      })
    } catch (error) {
      void showToast('error', error instanceof Error ? error.message : 'เกิดข้อผิดพลาดในการส่งรายการฝาก')
    } finally {
      setSubmitting(false)
    }
  }

  if ((!restoredDraft && !existingTransaction) || (!depositAccount && !isAttachSlipMode) || !flowLoaded || shouldUsePaymentGateway) {
    return null
  }

  return (
    <div className="content htd-slip">
      <div className="htd-slip__glow" aria-hidden />
      <div className="container">
        <div className="row">
          <div className="col-12 col-lg-10 offset-lg-1">
            <div className="row mx__1 mt-3 htd-slip__inner" style={{ rowGap: '1rem' }}>
              <div className="col-12 px-2">
                <div className="light_sd align-items-center bdc_primary p-3 p-md-4 border_30 htd-slip__hero htd-slip-anim htd-slip-anim--1" style={panelStyle}>
                  <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                    <div className="d-flex align-items-start gap-3">
                      <div className="htd-slip__hero-icon" aria-hidden>
                        <i className={allowSlipAttachment ? 'fa fa-receipt' : 'fa fa-sms'} />
                      </div>
                      <div>
                      <div className="small text-white-50 mb-1">
                        {isAttachSlipMode && slipStepLabels.length === 0
                          ? 'แนบสลิปให้รายการฝาก'
                          : `ขั้นตอนที่ ${slipStepIndex + 1} จาก ${slipStepTotal}`}
                      </div>
                      <h4 className="c_tertiary font_md mb-1">
                        {isPaymentAttachMode
                          ? 'แนบสลิป Payment'
                          : isAttachSlipMode
                            ? 'แนบสลิปให้รายการฝาก'
                            : isSmsOnlyManualDeposit
                              ? 'ส่งรายการฝากผ่าน SMS'
                              : 'ส่งรายการฝากและแนบสลิป'}
                      </h4>
                      <p className="text-white-50 mb-0">
                        {isPaymentAttachMode
                          ? 'หลังชำระเงินผ่าน QR แล้ว กรุณาแนบสลิปเพื่อส่งรายการฝากให้เป็นมาตรฐานเดียวกัน'
                          : isAttachSlipMode
                          ? 'คุณสามารถกลับมาแนบสลิปให้รายการฝากที่ยังรอดำเนินการได้จากหน้านี้'
                          : isSmsOnlyManualDeposit
                          ? 'บัญชีนี้รับรายการผ่าน SMS เท่านั้น จึงจะไม่มีขั้นตอนแนบสลิป และระบบจะรอจับคู่ SMS ให้แอดมินตรวจสอบ'
                          : submitWithoutSlipAllowed
                          ? 'คุณสามารถแนบสลิปเพื่อช่วยให้ระบบและแอดมินตรวจสอบได้เร็วขึ้น หรือส่งรายการฝากโดยยังไม่แนบสลิปก็ได้'
                          : 'กรุณาแนบสลิปก่อนส่งรายการฝาก เพื่อให้ระบบรับรายการตามนโยบายของเว็บนี้'}
                      </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {slipStepLabels.length > 0 && (
                <div className="col-12 px-2">
                  <div className="border_30 px-2 px-md-3 py-2 py-md-3 htd-slip__steps htd-slip-anim htd-slip-anim--2" style={innerSurfaceStyle}>
                    <div className="row mx__1" style={{ rowGap: '0.75rem' }}>
                      {slipStepLabels.map((label, index) => (
                        <div
                          key={label}
                          className={slipStepLabels.length === 2 ? 'col-12 col-md-6 px-1' : 'col-12 col-md-4 px-1'}
                        >
                          <div
                            className={`h-100 ${index === slipStepIndex ? 'c_tertiary' : 'c_primary'}`}
                            style={stepCardStyle(index === slipStepIndex)}
                          >
                            <div
                              className="small mb-1"
                              style={index === slipStepIndex ? { color: 'rgba(255,255,255,0.82)' } : lightSurfaceLabelStyle}
                            >
                              Step {index + 1}
                            </div>
                            <div className="fw-bold">{label}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="col-12 col-lg-5 px-2">
                <div className="light_sd align-items-center bdc_primary p-3 p-md-4 border_30 h-100 htd-slip__panel htd-slip-anim htd-slip-anim--3" style={panelStyle}>
                  <h5 className="c_tertiary font_md mb-3">ข้อมูลรายการ</h5>
                  <div className="d-grid gap-3">
                    <div style={valueCardStyle}>
                      <div className="small mb-1" style={lightSurfaceLabelStyle}>จำนวนเงิน</div>
                      <div className="c_primary" style={{ fontSize: '1.3rem', fontWeight: 800 }}>{Number(amount).toLocaleString()} บาท</div>
                    </div>
                    {showManualCustomerAccountCard ? (
                      <div style={valueCardStyle}>
                        <div className="small mb-1" style={lightSurfaceLabelStyle}>ใช้บัญชีที่สมัครโอนฝากเท่านั้น</div>
                        <div style={{ ...lightSurfaceHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>
                          {selectedUserBankAccount?.bankName || selectedSenderAccount?.bankName || '-'}
                        </div>
                        <div className="mt-1" style={lightSurfaceMutedTextStyle}>
                          {selectedUserBankAccount?.accountName || selectedSenderAccount?.accountName || '-'}
                        </div>
                        <div className="mt-2" style={{ color: '#b45309', fontWeight: 800 }}>
                          {selectedUserBankAccount?.accountNumber || selectedSenderAccount?.accountNumber || '-'}
                        </div>
                      </div>
                    ) : (
                      <div style={valueCardStyle}>
                        <div className="small mb-1" style={lightSurfaceLabelStyle}>ช่องทางฝาก</div>
                        <div style={{ ...lightSurfaceHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>{displayMethodLabel}</div>
                      </div>
                    )}
                    {!isPromptPaySlipMode && (
                      <div style={valueCardStyle}>
                        <div className="small mb-1" style={lightSurfaceLabelStyle}>
                          {isPaymentAttachMode ? 'ข้อมูลการชำระเงิน' : 'บัญชีปลายทางของเว็บ'}
                        </div>
                        <div style={{ ...lightSurfaceHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>
                          {isPaymentAttachMode ? paymentGateway?.bank || 'Payment' : depositAccount?.bankName || '-'}
                        </div>
                        <div className="mt-1" style={lightSurfaceMutedTextStyle}>
                          {isPaymentAttachMode ? 'แนบสลิปหลังชำระเงินผ่าน QR' : depositAccount?.accountName || '-'}
                        </div>
                        <div className="mt-2 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                          <div style={{ color: '#b45309', fontWeight: 800 }}>
                            {isPaymentAttachMode ? paymentGateway?.externalRefId || existingTransaction?._id || '-' : depositAccount?.accountNumber || '-'}
                          </div>
                          {(isPaymentAttachMode ? paymentGateway?.externalRefId || existingTransaction?._id : depositAccount?.accountNumber) && (
                            <button
                              type="button"
                              className="border-0 px-3 py-2 d-inline-flex align-items-center gap-2"
                              style={{
                                borderRadius: 999,
                                background: 'rgba(37, 99, 235, 0.10)',
                                color: '#1d4ed8',
                                fontWeight: 700,
                              }}
                              onClick={() => handleCopyAccountNumber(
                                isPaymentAttachMode
                                  ? paymentGateway?.externalRefId || existingTransaction?._id
                                  : depositAccount?.accountNumber
                              )}
                            >
                              <i className="fa fa-copy" aria-hidden="true" />
                              <span>Copy</span>
                            </button>
                          )}
                        </div>
                        {!isPaymentAttachMode && (
                          <div className="small mt-3" style={lightSurfaceMutedTextStyle}>
                            {selectedDepositSourceMode === 'sms_only'
                              ? 'บัญชีนี้ตั้งค่าเป็น SMS เท่านั้น'
                              : selectedDepositSourceMode === 'slip_only'
                                ? 'บัญชีนี้ตั้งค่าเป็น Slip เท่านั้น'
                                : 'บัญชีนี้รองรับทั้ง Slip และ SMS'}
                          </div>
                        )}
                      </div>
                    )}
                    {depositFlow.automationSettingsWarning && (
                      <div className="small text-warning">{depositFlow.automationSettingsWarning}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-7 px-2">
                <div className="light_sd align-items-center bdc_primary p-3 p-md-4 border_30 h-100 htd-slip__panel htd-slip-anim htd-slip-anim--4" style={panelStyle}>
                  <h5 className="c_tertiary font_md mb-3">
                    {isPaymentAttachMode
                      ? 'แนบสลิปการชำระเงิน (บังคับ)'
                      : isSmsOnlyManualDeposit
                        ? 'ส่งรายการแบบ SMS'
                        : isAttachSlipMode
                          ? 'แนบสลิปภายหลัง'
                          : `แนบสลิป ${submitWithoutSlipAllowed ? '(ไม่บังคับ)' : '(บังคับ)'}`}
                  </h5>
                  {allowSlipAttachment ? (
                    <div
                      className="border_30 px-3 py-4 text-center htd-slip__dropzone"
                      style={{
                        ...innerSurfaceStyle,
                        border: isDragOver ? '1px solid color-mix(in srgb, var(--htd-primary) 65%, transparent)' : '1px dashed color-mix(in srgb, var(--htd-primary) 35%, transparent)',
                        transform: isDragOver ? 'translateY(-1px)' : 'translateY(0)',
                        transition: 'all 0.2s ease',
                      }}
                      onDragOver={(event) => {
                        event.preventDefault()
                        setIsDragOver(true)
                      }}
                      onDragLeave={() => setIsDragOver(false)}
                      onDrop={(event) => {
                        event.preventDefault()
                        setIsDragOver(false)
                        const file = event.dataTransfer.files?.[0]
                        if (file) void validateAndSetFile(file)
                      }}
                    >
                      <div className="c_primary mb-2 htd-slip__upload-icon">
                        <i className="fa fa-cloud-upload-alt" style={{ fontSize: '2rem' }} />
                      </div>
                      <div className="c_primary font_md mb-2">ลากไฟล์มาวาง หรือเลือกสลิปจากเครื่อง</div>
                      <div className="small mb-3" style={lightSurfaceMutedTextStyle}>{SLIP_UPLOAD_HINT} (ระบบปรับขนาดอัตโนมัติเมื่อไฟล์ใหญ่)</div>
                      <button
                        type="button"
                        className="link_pmr box_row justify-content-center font_rg bg_primary border_30 border-0 mx-auto htd-slip__primary-btn"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <div className="d-flex align-items-center c_tertiary font_md">
                          <h5 className="mb-0">เลือกไฟล์สลิป</h5>
                        </div>
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        className="d-none"
                        onChange={(event) => {
                          const file = event.target.files?.[0]
                          if (!file) return
                          void validateAndSetFile(file, () => {
                            event.target.value = ''
                          })
                        }}
                      />
                    </div>
                  ) : null}

                  {allowSlipAttachment && previewUrl && (
                    <div className="mt-3">
                      <div className="small mb-2" style={lightSurfaceLabelStyle}>ตัวอย่างสลิป</div>
                      <div className="border_30 p-2" style={innerSurfaceStyle}>
                        <img src={previewUrl} alt="slip preview" className="img-fluid rounded-3" />
                      </div>
                    </div>
                  )}

                  <div className="mt-3 d-grid gap-2">
                    {allowSlipAttachment && (
                      <button
                        type="button"
                        className="link_pmr box_row justify-content-center font_rg bdc_primary bg_primary border_30 w-100 border-0 htd-slip__primary-btn"
                        disabled={submitting}
                        onClick={() => void submitDeposit(true)}
                      >
                        <div className="d-flex align-items-center c_tertiary font_md">
                          <h5 className="mb-0">
                            {submitting
                              ? 'กำลังส่งรายการ...'
                              : isAttachSlipMode
                                ? 'แนบสลิปให้รายการนี้'
                                : 'ส่งรายการฝากพร้อมแนบสลิป'}
                          </h5>
                        </div>
                      </button>
                    )}
                    {!isAttachSlipMode && isSmsOnlyManualDeposit ? (
                      <button
                        type="button"
                        className="link_pmr box_row justify-content-center font_rg bg_secondary border_30 w-100 border-0 htd-slip__secondary-btn"
                        onClick={() => navigate('/')}
                      >
                        <div className="d-flex align-items-center c_secondary font_md">
                          <h5 className="mb-0">กลับไปหน้าแรก</h5>
                        </div>
                      </button>
                    ) : (!isAttachSlipMode && submitWithoutSlipAllowed && (
                      <button
                        type="button"
                        className="link_pmr box_row justify-content-center font_rg bg_secondary border_30 w-100 border-0 htd-slip__secondary-btn"
                        disabled={submitting}
                        onClick={() => void submitDeposit(false)}
                      >
                        <div className="d-flex align-items-center c_secondary font_md">
                          <h5 className="mb-0">ส่งรายการฝากโดยยังไม่แนบสลิป</h5>
                        </div>
                      </button>
                    ))}
                    <button
                      type="button"
                      className="link_pmr box_row justify-content-center font_rg border_30 w-100 border-0 htd-slip__ghost-btn"
                      style={secondaryActionButtonStyle}
                      disabled={submitting}
                      onClick={() => {
                        if (isAttachSlipMode) {
                          navigate('/history/deposit')
                          return
                        }
                        navigate('/topup', {
                          state: { amount, depositAccount, depositMethod, usesPaymentGateway: requestedPaymentGateway },
                        })
                      }}
                    >
                      <div className="d-flex align-items-center c_tertiary font_md">
                        <h5 className="mb-0">{isAttachSlipMode ? 'กลับไปประวัติการฝาก' : 'กลับไปเลือกยอดฝาก'}</h5>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModernTopupSlipPage
